import React, { useEffect, useState } from 'react';
import axios from 'axios';
import Layout from './components/Layout';
import PageHeader from './components/PageHeader';
import PageContent from './components/PageContent';
import ResponsiveShadow from './components/ResponsiveShadow';
import useReferenceData from './components/hooks/useReferenceData';
import useDefaultModal from './components/hooks/useDefaultModal';
import useConfirmModal from './components/hooks/useConfirmModal';
import SreaAjusteModal from './components/modals/SreaAjusteModal';
import { API_BASE_URL } from '../config/ApiConfig';
import { formatCurrency } from './utils/formatMoeda';
import usePagination10 from './components/hooks/usePagination10';
import Toolbar from './components/Toolbar';

const emptyForm = { idPrograma: '', vlrC: '', vlrK: '' };

function SreaAjustes() {
  const token = localStorage.getItem('token');
  const cnpj = localStorage.getItem('cnpj');
  const ano = localStorage.getItem('ano');
  const { programas } = useReferenceData(token, cnpj);
  const { setDefaultModal, showDefaultModal } = useDefaultModal();
  const { setConfirmModal, showConfirmModal } = useConfirmModal();
  const [items, setItems] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState({ ...emptyForm });
  const [selected, setSelected] = useState([]);
  const [atualizar, setAtualizar] = useState(false);
  const { setPagination } = usePagination10(items.length);
  const totalVlrC = items.reduce((acc, it) => acc + (parseFloat(it.vlrC || 0) || 0), 0);
  const totalVlrK = items.reduce((acc, it) => acc + (parseFloat(it.vlrK || 0) || 0), 0);

  useEffect(() => {
    if (!token || !cnpj || !ano) return;
    axios.get(`${API_BASE_URL.SREA_AJUSTE}/search`, { params: { idEscola: cnpj, ano }, headers: { Authorization: `Bearer ${token}` } })
      .then(r => setItems(Array.isArray(r.data) ? r.data : []))
      .catch(() => showDefaultModal('error', 'Erro ao carregar ajustes SREA.'));
  }, [token, cnpj, ano, atualizar]);

  function handleChange(e) { const { name, value } = e.target; setForm(f => ({ ...f, [name]: value })); }
  function handleNew() { setForm({ ...emptyForm, _isEditing: false }); setShowModal(true); }
  function handleEdit() { if (selected.length !== 1) return; setForm({ ...selected[0], _isEditing: true }); setShowModal(true); }

  function handleSave(e) {
    e.preventDefault();
    const payload = { ...form, idEscola: cnpj, ano };
    axios.post(`${API_BASE_URL.SREA_AJUSTE}/save`, payload, { headers: { Authorization: `Bearer ${token}` } })
      .then(() => { setShowModal(false); setAtualizar(a => !a); setSelected([]); })
      .catch(err => {
        let msg = 'Erro ao salvar ajuste.';
        if (err.response && err.response.data && err.response.data.message) msg += '\n' + err.response.data.message;
        showDefaultModal('error', msg);
      });
  }

  function handleDelete() {
    if (selected.length === 0) return;
    showConfirmModal('delete', () => Promise.all(selected.map(s =>
      axios.delete(`${API_BASE_URL.SREA_AJUSTE}/${ano}/${s.idPrograma}/${cnpj}`, { headers: { Authorization: `Bearer ${token}` } })
    )).then(() => { setSelected([]); setAtualizar(a => !a); }).catch(() => showDefaultModal('error', 'Erro ao excluir ajuste.')));
  }

  function keyOf(item) { return `${ano}_${item.idPrograma}_${cnpj}`; }
  function toggleSelect(item) { setSelected(prev => prev.find(s => keyOf(s) === keyOf(item)) ? [] : [item]); }

  return (
    <Layout>
      <ResponsiveShadow>
        <PageHeader title="Ajustes SREA" icon="bi-sliders" />
        <PageContent>
          <Toolbar onNew={handleNew} onEdit={handleEdit} disableEdit={selected.length !== 1} onDelete={handleDelete} disableDelete={selected.length === 0} />

          <div className="table-responsive">
            <table className="table table-hover table-sm">
              <thead className="table-dark">
                <tr>
                  <th>#</th>
                  <th>Programa</th>
                  <th className="text-end">Valor Custeio</th>
                  <th className="text-end">Valor Capital</th>
                  <th className="text-end">Total</th>
                </tr>
              </thead>
              <tbody>
                {items.map((item, idx) => (
                  <tr key={keyOf(item)} className={selected.find(s => keyOf(s) === keyOf(item)) ? 'table-success' : ''} onClick={() => toggleSelect(item)} style={{ cursor: 'pointer' }}>
                    <td>{idx + 1}</td>
                    <td>{programas.find(p => p.id === item.idPrograma)?.descricao || item.idPrograma}</td>
                    <td className="text-end">{formatCurrency(item.vlrC)}</td>
                    <td className="text-end">{formatCurrency(item.vlrK)}</td>
                    <td className="text-end">{formatCurrency((parseFloat(item.vlrC || 0) || 0) + (parseFloat(item.vlrK || 0) || 0))}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="table-secondary fw-bold">
                  <td colSpan={2} className="text-end">Total geral</td>
                  <td className="text-end">{formatCurrency(totalVlrC)}</td>
                  <td className="text-end">{formatCurrency(totalVlrK)}</td>
                  <td className="text-end">{formatCurrency(totalVlrC + totalVlrK)}</td>
                </tr>
              </tfoot>
            </table>
          </div>
        </PageContent>
      </ResponsiveShadow>
      <SreaAjusteModal show={showModal} onClose={() => setShowModal(false)} onSubmit={handleSave} form={form} handleChange={handleChange} items={items} setForm={setForm} />
    </Layout>
  );
}

export default SreaAjustes;
